import { useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowRight,
  faChevronLeft,
  faChevronRight,
  faMagnifyingGlass,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'

import type { OreoBraDefinition } from '../data/oreoBras'
import { whatsAppCtaProps, trackSelectItem } from '../lib/analytics'
import { WA_NUMBER } from './SiteChrome'

export const OREO_CATALOG_PAGE_SIZE = 8
const LIST_NAME = 'Bra Oreo brand landing catalog'
const PAGE_CATEGORY = 'Bra Oreo'

function productMatches(product: OreoBraDefinition, query: string) {
  const normalizedQuery = query.trim().toLocaleLowerCase('id-ID')
  if (!normalizedQuery) return true

  return `${product.name} ${product.sku}`.toLocaleLowerCase('id-ID').includes(normalizedQuery)
}

export function getOreoCatalogPage(
  products: OreoBraDefinition[],
  query: string,
  requestedPage: number,
) {
  const filteredProducts = products.filter((product) => productMatches(product, query))
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / OREO_CATALOG_PAGE_SIZE))
  const currentPage = Math.min(Math.max(1, requestedPage), totalPages)
  const firstIndex = (currentPage - 1) * OREO_CATALOG_PAGE_SIZE

  return {
    filteredProducts,
    totalPages,
    currentPage,
    firstIndex,
    visibleProducts: filteredProducts.slice(firstIndex, firstIndex + OREO_CATALOG_PAGE_SIZE),
  }
}

function ProductLink({
  product,
  children,
  className,
  ariaLabel,
}: {
  product: OreoBraDefinition
  children: React.ReactNode
  className?: string
  ariaLabel?: string
}) {
  return (
    <a
      href={`/produk/${product.slug}`}
      aria-label={ariaLabel}
      className={className}
      onClick={() =>
        trackSelectItem(
          { item_id: product.sku, item_name: product.name, item_category: PAGE_CATEGORY },
          LIST_NAME,
        )
      }
    >
      {children}
    </a>
  )
}

function ProductCard({ product }: { product: OreoBraDefinition }) {
  const message = `Halo Dropify, saya mau cek ${product.name}, nomor model ${product.sku}. Kebutuhan: ___ lusin. Ukuran/warna: ___. Kota tujuan: ___. Bisa kirim foto stok dan harganya?`

  return (
    <article className="group flex flex-col overflow-hidden rounded-3xl border border-rose-100 bg-white transition-all hover:-translate-y-1 hover:border-rose-300 hover:shadow-xl">
      <ProductLink product={product} ariaLabel={`Lihat detail ${product.name}`} className="relative block overflow-hidden bg-rose-50">
        <img src={product.image} alt={product.name} width="640" height="640" loading="lazy" decoding="async" className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
      </ProductLink>
      <div className="flex flex-1 flex-col p-5">
        <p className="break-all text-[11px] font-bold uppercase tracking-wide text-rose-700">{product.sku}</p>
        <h3 className="mt-2 text-xl font-black tracking-tight">
          <ProductLink product={product} className="hover:text-rose-700">{product.name}</ProductLink>
        </h3>
        <ProductLink product={product} className="mt-3 inline-flex items-center gap-2 text-sm font-black text-rose-700">
          Lihat detail produk <FontAwesomeIcon icon={faArrowRight} className="h-3.5 w-3.5" />
        </ProductLink>
        <a
          href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`}
          target="_blank"
          rel="noopener noreferrer"
          {...whatsAppCtaProps('landing_page', {
              cta_placement: 'product_card',
              lp_category: PAGE_CATEGORY,
              lead_item_name: product.name,
              items: [{ item_id: product.sku, item_name: product.name, item_category: PAGE_CATEGORY }],
            })}
          className="mt-5 inline-flex items-center justify-center gap-2 rounded-xl bg-green-700 px-4 py-3 text-sm font-black text-white transition-colors hover:bg-green-800"
        >
          <FontAwesomeIcon icon={faWhatsapp} className="h-5 w-5 flex-shrink-0" />
          Tanya {product.name}
        </a>
      </div>
    </article>
  )
}

export function OreoProductCatalog({ products }: { products: OreoBraDefinition[] }) {
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const { filteredProducts, totalPages, currentPage, firstIndex, visibleProducts } = useMemo(
    () => getOreoCatalogPage(products, query, page),
    [products, query, page],
  )

  const changePage = (nextPage: number) => {
    setPage(nextPage)
    document.getElementById('katalog-bra-oreo')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }

  const updateQuery = (value: string) => {
    setQuery(value)
    setPage(1)
  }

  return (
    <div id="katalog-bra-oreo" className="scroll-mt-6">
      <div className="mt-8 flex flex-col gap-4 rounded-2xl border border-rose-100 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
        <label className="relative block w-full sm:max-w-md">
          <span className="sr-only">Cari bra Oreo berdasarkan nama atau nomor model</span>
          <FontAwesomeIcon icon={faMagnifyingGlass} className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            value={query}
            onChange={(event) => updateQuery(event.target.value)}
            placeholder="Cari nama atau nomor model"
            className="w-full rounded-xl border border-rose-100 bg-[#fff8f8] py-3 pl-11 pr-11 text-sm text-[#2b1118] outline-none transition focus:border-rose-400 focus:ring-4 focus:ring-rose-100"
          />
          {query && (
            <button type="button" onClick={() => updateQuery('')} aria-label="Hapus pencarian" className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-slate-500 hover:bg-rose-100 hover:text-rose-800">
              <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
            </button>
          )}
        </label>
        <p className="flex-shrink-0 text-sm font-bold text-slate-600" aria-live="polite">
          {query.trim()
            ? `${filteredProducts.length} model ditemukan`
            : `Menampilkan ${firstIndex + 1}–${Math.min(firstIndex + OREO_CATALOG_PAGE_SIZE, filteredProducts.length)} dari ${filteredProducts.length} model`}
        </p>
      </div>

      {visibleProducts.length > 0 ? (
        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {visibleProducts.map((product) => <ProductCard key={product.sku} product={product} />)}
        </div>
      ) : (
        <div className="mt-6 rounded-3xl border border-rose-100 bg-white px-5 py-14 text-center">
          <p className="font-black text-[#2b1118]">Model “{query.trim()}” tidak ditemukan.</p>
          <p className="mt-2 text-sm text-slate-600">Coba masukkan nama atau nomor model.</p>
          <button type="button" onClick={() => updateQuery('')} className="mt-5 rounded-xl border border-rose-200 px-5 py-2.5 text-sm font-black text-rose-800 hover:bg-rose-50">Hapus pencarian</button>
        </div>
      )}

      {totalPages > 1 && (
        <nav aria-label="Halaman katalog bra Oreo" className="mt-9 flex flex-wrap items-center justify-center gap-2">
          <button type="button" onClick={() => changePage(currentPage - 1)} disabled={currentPage === 1} className="inline-flex h-11 items-center gap-2 rounded-xl border border-rose-200 bg-white px-4 text-sm font-black text-[#3d1020] hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-40">
            <FontAwesomeIcon icon={faChevronLeft} className="h-3.5 w-3.5" /> Sebelumnya
          </button>
          {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
            <button key={pageNumber} type="button" onClick={() => changePage(pageNumber)} aria-label={`Halaman ${pageNumber}`} aria-current={pageNumber === currentPage ? 'page' : undefined} className={`h-11 min-w-11 rounded-xl border px-3 text-sm font-black ${pageNumber === currentPage ? 'border-[#3d1020] bg-[#3d1020] text-white' : 'border-rose-200 bg-white text-[#3d1020] hover:bg-rose-50'}`}>
              {pageNumber}
            </button>
          ))}
          <button type="button" onClick={() => changePage(currentPage + 1)} disabled={currentPage === totalPages} className="inline-flex h-11 items-center gap-2 rounded-xl border border-rose-200 bg-white px-4 text-sm font-black text-[#3d1020] hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-40">
            Berikutnya <FontAwesomeIcon icon={faChevronRight} className="h-3.5 w-3.5" />
          </button>
        </nav>
      )}
    </div>
  )
}
